// Reference counting for the escaped generation — stage 2 of the memory-model
// rewrite (docs/memory-model.md §"Stage 2 mechanism").
//
// Escaped objects (heap objects a global holds, so they outlive the event) are
// reclaimed by reference count: how many PERSISTENT references point at an
// object. Persistent = a global slot, or an element of an escaped container.
// Locals never count — they are young and vanish at the event rewind, so they
// can never leave a dangling count.
//
// The compiler drives three operations at every store into a persistent slot,
// passing the slot's element shape as constants (it knows the static type):
//
//   promoteEscaped(value)  — move a young value into the escaped generation so
//                            it survives the rewind; an already-escaped value
//                            is returned as-is (this is how aliasing works).
//   retain(obj)            — a new persistent reference now points at `obj`.
//   release(obj)           — a persistent reference is gone; at zero, free it
//                            and cascade a release to each heap element.
//
// The discipline at `g = value` is: promote, retain the (promoted) new value,
// store it, release the old value. Retain-before-release so a self-assignment
// or an alias can't be freed and then used.
import { HEADER_SIZE, OFF_PTR, OFF_SIZE, OFF_LENGTH, OFF_REFCOUNT, arrData, arrSize, arrLength, } from "./memory-utils.js";
// A new persistent reference points at `obj`. No-op on the null pointer.
export function retain(h, obj) {
    if (obj === 0)
        return;
    const rc = h.view.getInt32(obj + OFF_REFCOUNT, true);
    h.view.setInt32(obj + OFF_REFCOUNT, rc + 1, true);
}
// A persistent reference to `obj` is gone. When the count reaches zero the
// object is dead: cascade a release to each heap element it holds (one level —
// nested composites are banned, so an element's own elements are always
// scalars), then return the data block and header to the escaped free lists.
//
// `elemStride` is the byte width of one element (4 for a pointer element, 1 for
// a string byte, etc.); `elementIsHeap` is true only for a container whose
// elements are themselves escaped objects (e.g. `string[]`), whose refcounts
// must be dropped too.
export function release(h, esc, obj, elemStride, elementIsHeap) {
    // Null, or a non-escaped pointer, is not reference-counted: a global's very
    // first value is whatever `init` put there (a young/init object below the
    // first event's mark), and only becomes escaped once reassigned. Releasing
    // that initial value must be a harmless no-op, never a free of memory the
    // escaped free lists don't own.
    if (obj === 0 || !esc.containsPointer(obj))
        return;
    const rc = h.view.getInt32(obj + OFF_REFCOUNT, true) - 1;
    h.view.setInt32(obj + OFF_REFCOUNT, rc, true);
    if (rc > 0)
        return;
    // Dead. Cascade to heap elements first, while the data block is still valid.
    const data = arrData(h, obj);
    const capacity = arrSize(h, obj);
    if (elementIsHeap) {
        const length = arrLength(h, obj);
        for (let i = 0; i < length; i++) {
            const child = h.view.getInt32(data + i * elemStride, true);
            // A container's heap element is always a leaf here (a string): stride 1,
            // no further cascade.
            release(h, esc, child, 1, false);
        }
    }
    esc.free(data, capacity * elemStride);
    esc.free(obj, HEADER_SIZE);
}
// Ensure `obj` lives in the escaped generation, returning its escaped address.
//
//   - null            → null.
//   - already escaped → returned unchanged. Storing it into a second global
//                       therefore SHARES the one object (the caller's retain
//                       bumps its count) — real pass-by-reference aliasing.
//   - young           → deep-copied into the escaped generation (header + data,
//                       and each heap element promoted + retained), because the
//                       young original is about to be rewound. The copy starts
//                       at refcount 0; the caller's retain establishes the first
//                       reference.
// In-place reassignment of a persistent string slot (docs/memory-model.md
// §"Open follow-ons"). The plain `global = newString` barrier does
// promote-new → retain → release-old → store: it allocates a fresh escaped
// block and frees the old one every reassignment, even though (post the
// reserve-at-capacity work) the outgoing slot is a fixed-size buffer of the
// same size each time. When that outgoing value is an escaped string NOTHING
// ELSE references (refcount == 1) and its buffer is large enough to hold the
// incoming bytes, we overwrite it in place and skip the alloc/free round-trip:
// the slot keeps pointing at the same block.
//
// Sound only under refcount == 1 — a string aliased into another persistent
// slot must not be mutated underneath its other holders, so that case falls
// back to promote/release. The capacity guard (`size >= newLen`) means the
// buffer never overflows; a reserved `string<N>` slot always holds an N-byte
// buffer and the incoming reserved value is also N bytes, so the fast path is
// the common case. Returns the pointer the slot should now hold — the reused
// old block, or a freshly promoted one.
export function reassignEscapedString(h, esc, oldPtr, newPtr) {
    if (oldPtr !== 0 &&
        esc.containsPointer(oldPtr) &&
        h.view.getInt32(oldPtr + OFF_REFCOUNT, true) === 1) {
        const newLen = arrLength(h, newPtr);
        if (arrSize(h, oldPtr) >= newLen) {
            const oldData = arrData(h, oldPtr);
            const newData = arrData(h, newPtr);
            for (let i = 0; i < newLen; i++) {
                h.view.setUint8(oldData + i, h.view.getUint8(newData + i));
            }
            h.view.setInt32(oldPtr + OFF_LENGTH, newLen, true);
            return oldPtr; // reused in place; refcount stays 1, slot unchanged
        }
    }
    // Fallback: promote the incoming value, add the new persistent reference,
    // drop the outgoing one (freeing it if it was the last).
    const escNew = promoteEscaped(h, esc, newPtr, 1, false);
    retain(h, escNew);
    release(h, esc, oldPtr, 1, false);
    return escNew;
}
export function promoteEscaped(h, esc, obj, elemStride, elementIsHeap) {
    if (obj === 0)
        return 0;
    if (esc.containsPointer(obj))
        return obj;
    const capacity = arrSize(h, obj);
    const length = arrLength(h, obj);
    const youngData = arrData(h, obj);
    const escHeader = esc.alloc(HEADER_SIZE);
    const dataBytes = capacity * elemStride;
    const escData = esc.alloc(dataBytes);
    // Copy the raw data block verbatim; heap-element pointers in it are fixed up
    // below.
    for (let i = 0; i < dataBytes; i++) {
        h.view.setUint8(escData + i, h.view.getUint8(youngData + i));
    }
    h.view.setInt32(escHeader + OFF_PTR, escData, true);
    h.view.setInt32(escHeader + OFF_SIZE, capacity, true);
    h.view.setInt32(escHeader + OFF_LENGTH, length, true);
    h.view.setInt32(escHeader + OFF_REFCOUNT, 0, true);
    if (elementIsHeap) {
        // Each element is a reference to a child object; promote it and record the
        // new container→child reference with a retain.
        for (let i = 0; i < length; i++) {
            const childYoung = h.view.getInt32(youngData + i * elemStride, true);
            const childEsc = promoteEscaped(h, esc, childYoung, 1, false);
            h.view.setInt32(escData + i * elemStride, childEsc, true);
            retain(h, childEsc);
        }
    }
    return escHeader;
}
//# sourceMappingURL=escaped-refcount.js.map